import { useState } from "react";
import { useIntl } from "react-intl";
import { useMediaQuery } from "@wayflyer/flyui";
import { IconArrowLeft24Line, IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { DOCUMENT_NAMES, DOCUMENT_TEMPLATES } from "../../../components/incomplete-steps-modal/message-definitions";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { useApplicationSubmit } from "../../../hooks/use-application-submit";

type UseDocumentNavigationActionsOptions = {
  forType: string;
  onPrimaryAction?: () => void;
};

export const useDocumentNavigationActions = ({ forType, onPrimaryAction }: UseDocumentNavigationActionsOptions) => {
  const intl = useIntl();
  const query = useMediaQuery();
  const isMobile = query("sm").windowLte;
  const navigationContext = useFundingApplicationNavigation({
    currentPage: PAGE_TYPE.DOCUMENTS,
    currentStep: forType,
  });
  const applicationSubmit = useApplicationSubmit({
    currentPage: PAGE_TYPE.DOCUMENTS,
    currentStep: forType,
  });
  const [isPrimarySubmitting, setIsPrimarySubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const isDocumentComplete = applicationSubmit.stageCompletionStatus.documents?.[forType] ?? false;

  const nameDescriptor = DOCUMENT_NAMES[forType as keyof typeof DOCUMENT_NAMES];
  const documentError =
    hasAttemptedSubmit && !isDocumentComplete
      ? nameDescriptor
        ? intl.formatMessage(DOCUMENT_TEMPLATES.inlineError, { documentName: intl.formatMessage(nameDescriptor) })
        : intl.formatMessage(COMMON_MESSAGES.pleaseFillOutThisField)
      : undefined;

  const primaryAction = {
    content: navigationContext.nextButtonLabel,
    icon: IconArrowRight24Line,
    loading: isPrimarySubmitting || applicationSubmit.isSubmitting,
    analyticsId: "document-uploads-next-button",
    onAction: async () => {
      if (!navigationContext.nextPath) {
        if (!isDocumentComplete) {
          setHasAttemptedSubmit(true);
          return;
        }
        applicationSubmit.submitApplication();
        return;
      }
      setIsPrimarySubmitting(true);
      try {
        await navigationContext.navigateWithStateUpdate(navigationContext.nextPath);
      } finally {
        setIsPrimarySubmitting(false);
      }
      onPrimaryAction?.();
    },
  };

  const secondaryAction = {
    content: isMobile ? undefined : navigationContext.backButtonLabel,
    icon: IconArrowLeft24Line,
    analyticsId: "document-uploads-back-button",
    onAction: () => {
      if (navigationContext.previousPath) {
        navigationContext.navigate(navigationContext.previousPath);
      }
    },
  };

  return { primaryAction, secondaryAction, applicationSubmit, documentError };
};
